import React from 'react';
import styled from 'styled-components';

interface AvatarWrapperProps {
  $size: number;
  $rounded: boolean;
}

const AvatarWrapper = styled.div<AvatarWrapperProps>`
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  border-radius: ${p => (p.$rounded ? '50%' : '12px')};
  background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  text-transform: uppercase;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 2px solid #ffffff;
  position: relative;
  font-size: ${p => p.$size * 0.4}px;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: inset 0 2px 4px rgba(255, 255, 255, 0.3);
    pointer-events: none;
  }
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export type AvatarProps = {
  size?: number;
  src?: string | null;
  fallbackSrc?: string | null;
  initials?: string;
  alt?: string;
  rounded?: boolean;
  className?: string;
  onClick?: () => void;
};

const Avatar: React.FC<AvatarProps> = ({
  size = 32,
  src,
  fallbackSrc,
  initials = '',
  alt = 'Profile photo',
  rounded = true,
  className,
  onClick,
}) => {
  const [failed, setFailed] = React.useState(false);
  const [currentSrc, setCurrentSrc] = React.useState<string | null>(src || fallbackSrc || null);

  React.useEffect(() => {
    setFailed(false);
    setCurrentSrc(src || fallbackSrc || null);
  }, [src, fallbackSrc]);

  const handleError = () => {
    if (currentSrc && src && currentSrc === src && fallbackSrc) {
      setCurrentSrc(fallbackSrc);
    } else {
      setFailed(true);
    }
  };

  const showInitials = failed || !currentSrc || currentSrc === '';
  return (
    <AvatarWrapper $size={size} $rounded={rounded} className={className} onClick={onClick}>
      {showInitials ? (
        <span aria-hidden="true">{(initials || '').slice(0, 2)}</span>
      ) : (
        <AvatarImg src={currentSrc as string} alt={alt} onError={handleError} />
      )}
    </AvatarWrapper>
  );
};

export default Avatar;
